import { Box, Paper, Stack, TextField, Typography, Modal, IconButton } from '@mui/material';
import { Close } from '@sweep-io/sweep-design/dist/icons/Close';
import { DemoCommand, ChatCommandMapping, DelayConfiguration } from '../types/demoConsts';
import useEventListener from '../../components/common/useEventListener';

interface ChatCommandMappingPanelProps {
  commandMapping: ChatCommandMapping;
  onUpdateCommand: (command: DemoCommand, newValue: string) => void;
  delayConfiguration: DelayConfiguration;
  onUpdateDelay: (command: keyof DelayConfiguration, delaySeconds: number) => void;
  open: boolean;
  onClose: () => void;
}

// Shorter descriptions for compact display
const COMPACT_DESCRIPTIONS: Record<DemoCommand, string> = {
  [DemoCommand.ADD_HEALTHCARE_FUNNELS]: 'Add Funnels',
  [DemoCommand.ADD_TREATMENT_REVIEW_STAGE]: 'Add Stage',
  [DemoCommand.ADD_METADATA_AND_CONNECT]: 'Add Metadata',
  [DemoCommand.RESET_DEMO]: 'Reset Demo',
};

// Commands that support delay configuration (excluding reset)
const DELAY_SUPPORTED_COMMANDS: (keyof DelayConfiguration)[] = [
  DemoCommand.ADD_HEALTHCARE_FUNNELS,
  DemoCommand.ADD_TREATMENT_REVIEW_STAGE,
  DemoCommand.ADD_METADATA_AND_CONNECT,
];

export const DemoSettingsPanel = ({
  commandMapping,
  onUpdateCommand,
  delayConfiguration,
  onUpdateDelay,
  open,
  onClose,
}: ChatCommandMappingPanelProps) => {
  // Handle keyboard shortcuts
  const handleKeyDown = (event: KeyboardEvent) => {
    // F2 to toggle modal (safe across all browsers and platforms)
    if (event.key === 'F2') {
      event.preventDefault();
      if (open) {
        onClose();
      } else {
        // This will be handled by the parent component
      }
    }
    // Escape to close modal
    if (event.key === 'Escape' && open) {
      onClose();
    }
  };

  useEventListener('keydown', handleKeyDown);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 3,
          backgroundColor: 'white',
          borderRadius: 2,
          minWidth: 400,
          maxWidth: 500,
          border: '1px solid',
          borderColor: 'grey.200',
          outline: 'none',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="600" color="text.primary">
            Demo Settings
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
          >
            <Close />
          </IconButton>
        </Box>

      {/* Column Headers */}
      <Stack direction="row" spacing={1} mb={1}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Chat Command
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ width: 80, textAlign: 'center' }}>
          Delay (seconds)
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {Object.values(DemoCommand).map((command) => (
          <Box key={command}>
            <Typography
              variant="caption"
              fontWeight="500"
              color="text.secondary"
              mb={0.5}
              display="block"
            >
              {COMPACT_DESCRIPTIONS[command]}
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                value={commandMapping[command]}
                onChange={(e) => onUpdateCommand(command, e.target.value)}
                placeholder="command..."
                sx={{
                  '& .MuiInputBase-root': {
                    height: 32,
                    fontSize: '0.875rem',
                  },
                }}
              />
              {DELAY_SUPPORTED_COMMANDS.includes(command as keyof DelayConfiguration) ? (
                <TextField
                  size="small"
                  type="number"
                  value={delayConfiguration[command as keyof DelayConfiguration]}
                  onChange={(e) => {
                    const value = Math.max(0, parseInt(e.target.value) || 0);
                    onUpdateDelay(command as keyof DelayConfiguration, value);
                  }}
                  placeholder="0"
                  inputProps={{ min: 0, step: 1 }}
                  sx={{
                    width: 80,
                    '& .MuiInputBase-root': {
                      height: 32,
                      fontSize: '0.875rem',
                    },
                  }}
                />
              ) : (
                <Box sx={{ width: 80 }} />
              )}
            </Stack>
          </Box>
        ))}
      </Stack>

        <Box mt={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="caption" color="textSecondary" fontSize="0.75rem" mb={0.5} display="block">
            💬 Type these commands in chat to trigger actions
          </Typography>
          <Typography variant="caption" color="textSecondary" fontSize="0.7rem">
            ⌨️ Press <strong>F2</strong> to toggle this panel
          </Typography>
        </Box>
      </Paper>
    </Modal>
  );
};
